import { request, unwrap } from '../client';
import type {
  ApiResponse,
  CreateOidcClientRequest,
  EffectiveMembersResponse,
  OidcClientCreateResponse,
  OidcClientResponse,
  RotateSecretResponse,
} from '../types';

const BASE = '/api/saas-admin/oauth-clients';

export const oidcClientsApi = {
  async list(): Promise<OidcClientResponse[]> {
    const res = await request<ApiResponse<OidcClientResponse[]>>('GET', BASE);
    return unwrap(res);
  },

  async get(id: string): Promise<OidcClientResponse> {
    const res = await request<ApiResponse<OidcClientResponse>>('GET', `${BASE}/${id}`);
    return unwrap(res);
  },

  async create(req: CreateOidcClientRequest): Promise<OidcClientCreateResponse> {
    const res = await request<ApiResponse<OidcClientCreateResponse>>('POST', BASE, {
      json: req,
    });
    return unwrap(res);
  },

  async rotateSecret(id: string): Promise<RotateSecretResponse> {
    const res = await request<ApiResponse<RotateSecretResponse>>(
      'POST',
      `${BASE}/${id}/rotate-secret`,
    );
    return unwrap(res);
  },

  async disable(id: string): Promise<OidcClientResponse> {
    const res = await request<ApiResponse<OidcClientResponse>>(
      'POST',
      `${BASE}/${id}/disable`,
    );
    return unwrap(res);
  },

  // Spec 0061 (saas-auth SASATH-0078) — per-product access scope replaces the
  // per-tenant allow-list. The `…/allowed-tenants` add/remove calls are REMOVED;
  // these `…/allowed-products` calls take their place (same base path, envelope,
  // and error handling — only the noun changes tenant→product). `list` returns
  // the bare allowed product ids (mirrors the client read's `allowedProductIds`);
  // add/remove return the updated client (mirrors the former add/remove-tenant).
  async listAllowedProducts(id: string): Promise<number[]> {
    const res = await request<ApiResponse<number[]>>(
      'GET',
      `${BASE}/${id}/allowed-products`,
    );
    return unwrap(res);
  },

  async addAllowedProduct(id: string, productId: number): Promise<OidcClientResponse> {
    const res = await request<ApiResponse<OidcClientResponse>>(
      'POST',
      `${BASE}/${id}/allowed-products`,
      { json: { productId } },
    );
    return unwrap(res);
  },

  async removeAllowedProduct(id: string, productId: number): Promise<OidcClientResponse> {
    const res = await request<ApiResponse<OidcClientResponse>>(
      'DELETE',
      `${BASE}/${id}/allowed-products/${productId}`,
    );
    return unwrap(res);
  },

  // Spec 0038 — derived effective-members view. Display-only; any error (notably the
  // pre-0040 404) resolves to null so the screen shows a quiet placeholder.
  // NOTE: built on the existing `/oauth-clients` base (every client endpoint here uses
  // it); the handoff wrote "oidc-clients" — reconcile at saas-auth 0040 ship if it differs.
  async effectiveMembers(id: string): Promise<EffectiveMembersResponse | null> {
    try {
      const res = await request<ApiResponse<EffectiveMembersResponse>>(
        'GET',
        `${BASE}/${id}/effective-members`,
      );
      return unwrap(res);
    } catch {
      return null;
    }
  },
};
